import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, KeyRound, Eye, EyeOff, Check } from "lucide-react";
import api from "../../lib/apiClient";
import { useAuth } from "../../lib/auth";

export default function SettingsAccount() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);

  const strength = (() => {
    if (!next) return { label: "", color: "" };
    const long = next.length >= 12;
    const hasUpper = /[A-Z]/.test(next);
    const hasLower = /[a-z]/.test(next);
    const hasDigit = /\d/.test(next);
    const hasSym = /[^A-Za-z0-9]/.test(next);
    const score = [long, hasUpper, hasLower, hasDigit, hasSym].filter(Boolean).length;
    if (next.length < 8) return { label: "Too short (min 8 chars)", color: "text-red-600" };
    if (score <= 2) return { label: "Weak", color: "text-orange-600" };
    if (score === 3) return { label: "Fair", color: "text-yellow-600" };
    if (score === 4) return { label: "Good", color: "text-emerald-600" };
    return { label: "Strong", color: "text-emerald-700" };
  })();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOk(false);
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("New password and confirmation do not match."); return; }
    if (next === current) { setErr("New password must be different from your current password."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setOk(true);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Could not change password.");
    } finally {
      setBusy(false);
    }
  };

  const _inputCls = "w-full px-3 py-2.5 text-base border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:border-[#0F4C81] focus:ring-1 focus:ring-[#0F4C81]/20 bg-white dark:bg-slate-900 min-h-[44px]";

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/settings" className="text-sm text-slate-500 hover:text-[#0F4C81] flex items-center gap-1" data-testid="settings-account-back">
          <ChevronLeft size={16} /> Settings
        </Link>
      </div>

      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-slate-900 dark:text-slate-100">Account</h1>
        <p className="text-sm text-slate-500 mt-1">Signed in as <b>{user?.email}</b></p>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={18} className="text-[#0F4C81]" />
          <h2 className="font-display font-semibold text-slate-900 dark:text-slate-100">Change Password</h2>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-slate-500 font-medium mb-1">Current password</span>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoComplete="current-password"
                required
                className={_inputCls + " pr-10"}
                data-testid="current-password-input"
              />
              <button type="button" onClick={() => setShowCurrent(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600" tabIndex={-1} aria-label="Toggle current password visibility">
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </label>

          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-slate-500 font-medium mb-1">New password</span>
            <div className="relative">
              <input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                autoComplete="new-password"
                required
                minLength={8}
                className={_inputCls + " pr-10"}
                data-testid="new-password-input"
              />
              <button type="button" onClick={() => setShowNext(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600" tabIndex={-1} aria-label="Toggle new password visibility">
                {showNext ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {strength.label && (
              <div className={`text-xs mt-1.5 ${strength.color}`} data-testid="password-strength">
                Strength: {strength.label}
              </div>
            )}
          </label>

          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-slate-500 font-medium mb-1">Confirm new password</span>
            <input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              className={_inputCls}
              data-testid="confirm-password-input"
            />
            {confirm && next && confirm !== next && (
              <div className="text-xs mt-1.5 text-red-600">Passwords do not match</div>
            )}
          </label>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 rounded" data-testid="change-password-error">
              {err}
            </div>
          )}

          {ok && (
            <div className="text-sm text-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 px-3 py-2 rounded flex items-center gap-2" data-testid="change-password-success">
              <Check size={16} /> Password updated. Your existing session stays signed in — you&apos;ll need the new password on next login.
            </div>
          )}

          <div className="pt-2 flex items-center gap-3">
            <button
              type="submit"
              disabled={busy || !current || !next || !confirm}
              className="px-5 py-2.5 min-h-[44px] bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm font-semibold rounded-md disabled:opacity-60 transition-colors"
              data-testid="change-password-submit"
            >
              {busy ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>

        <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700 text-xs text-slate-500 space-y-1">
          <div>• Minimum 8 characters. Use 12+ with mixed case, digits, and symbols for a strong password.</div>
          <div>• Your existing sessions stay signed in after a password change.</div>
          <div>• Lost your password? Log out and use &quot;Forgot password&quot; on the login screen.</div>
        </div>
      </div>
    </div>
  );
}
